import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import formsAPI from '@/api/forms';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { normalizeData, normalizeIncluded } from '@/utils/jsonapi';

/**
 * Entity adapter for data entry forms
 */
const formsAdapter = createEntityAdapter({
  selectId: (form) => form.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Async thunk to fetch all forms
 * Normalizes JSON:API response before storing in Redux
 */
export const fetchFormsThunk = createAsyncThunk(
  'forms/fetchAll',
  async (queryOptions = {}, { rejectWithValue, getState, signal }) => {
    try {
      const tenantId = getState().auth?.currentTenantId;
      const requestOptions = { signal, ...(tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {}) };
      const response = queryOptions.page != null
        ? await formsAPI.getAll(queryOptions, requestOptions)
        : await formsAPI.getAllPages(queryOptions, requestOptions);

      // Handle both DRF pagination and JSON:API formats
      const results = response.results || response.data || [];

      // Normalize each JSON:API resource in the results array
      const normalized = normalizeData(results);

      return {
        data: normalized || [],
        count: response.meta?.pagination?.count ?? response.count,
        next: response.links?.next ?? response.next,
        previous: response.links?.prev ?? response.previous,
        included: response.included
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to fetch a single form by ID
 * Normalizes JSON:API response before storing in Redux
 */
export const fetchFormDetailThunk = createAsyncThunk(
  'forms/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await formsAPI.getById(id);

      // Normalize single resource
      const normalized = normalizeData(jsonApiResponse.data);

      // Process included resources for form_metrics, metrics, etc.
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Attach form_metrics with full metric data
      if (included.form_metric) {
        const formMetrics = Object.values(included.form_metric).map(fm => {
          // Get the full metric data from included
          const metricData = included.metric?.[fm.metric?.id || fm.metric] || {};

          // Process partition_configurations - these are metric_partition resources
          let partitionConfigs = metricData.partition_configurations || [];
          if (Array.isArray(partitionConfigs)) {
            partitionConfigs = partitionConfigs.map(configRef => {
              // configRef is { type: 'metric_partition', id: '...' }
              const configId = configRef?.id || configRef;
              const configData = included.metric_partition?.[configId] || {};

              // Get the partition type from included
              const partitionTypeRef = configData.partition_type;
              const partitionTypeId = partitionTypeRef?.id || partitionTypeRef;
              const partitionTypeData = included.partition_type?.[partitionTypeId] || {};

              // Get partition values for this partition type
              let partitionValues = [];
              const pvRefs = partitionTypeData.partition_values || [];
              if (Array.isArray(pvRefs)) {
                partitionValues = pvRefs.map(pvRef => {
                  const pvId = pvRef?.id || pvRef;
                  return included.partition_value?.[pvId] || { id: pvId };
                });
              }

              return {
                ...configData,
                partition_type: partitionTypeData,
                partition_type_name: partitionTypeData.name || 'Partition',
                partition_values: partitionValues
              };
            });
          }

          return {
            ...fm,
            metric: {
              ...metricData,
              // Attach category if available
              category: included.category?.[metricData.category?.id || metricData.category] || metricData.category,
              // Attach processed partition_configurations
              partition_configurations: partitionConfigs
            }
          };
        });

        // Sort by sort_order
        formMetrics.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        normalized._formMetrics = formMetrics;
      }

      return normalized;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to create a new form
 * Normalizes JSON:API response before storing in Redux
 */
export const createFormThunk = createAsyncThunk(
  'forms/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await formsAPI.create(data);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to update an existing form
 * Normalizes JSON:API response before storing in Redux
 */
export const updateFormThunk = createAsyncThunk(
  'forms/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await formsAPI.update(id, data);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Save which partition values this form should open with.
 *
 * Applies the change locally first so the control updates instantly, then
 * persists. Deliberately does not go through updateFormThunk: that replaces
 * currentForm with the PATCH response, whose relationships are not hydrated,
 * so the fill page loses its metrics and visibly collapses mid-save.
 *
 * Reverts on failure, so a rejected save cannot leave the UI claiming
 * something the server did not accept.
 */
export const saveDefaultPartitionSelectionThunk = createAsyncThunk(
  'forms/saveDefaultPartitionSelection',
  async ({ id, selection }, { dispatch, getState, rejectWithValue }) => {
    const previous = selectCurrentForm(getState())?.default_partition_selection || {};
    dispatch(defaultPartitionSelectionChanged({ id, selection }));

    try {
      await formsAPI.update(id, { default_partition_selection: selection });
      return { id, selection };
    } catch (error) {
      dispatch(defaultPartitionSelectionChanged({ id, selection: previous }));
      return rejectWithValue(
        getApiErrorMessage(error, 'Could not save what this form opens with')
      );
    }
  }
);

/**
 * Async thunk to delete (deactivate) a form
 */
export const deleteFormThunk = createAsyncThunk(
  'forms/delete',
  async (id, { rejectWithValue }) => {
    try {
      await formsAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to duplicate a form
 */
export const duplicateFormThunk = createAsyncThunk(
  'forms/duplicate',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await formsAPI.duplicate(id, { name });
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to reorder metrics within a form
 */
export const reorderFormMetricsThunk = createAsyncThunk(
  'forms/reorderMetrics',
  async ({ formId, metricIds }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await formsAPI.reorderMetrics(formId, metricIds);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to fetch available metrics for form creation
 */
export const fetchAvailableMetricsThunk = createAsyncThunk(
  'forms/fetchAvailableMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await formsAPI.getAvailableMetrics();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Forms slice with state management for form CRUD operations
 */
const formsSlice = createSlice({
  name: 'forms',
  initialState: formsAdapter.getInitialState({
    currentForm: null,
    catalogRequestId: null,
    availableMetrics: [],
    loading: false,
    error: null,
  }),
  reducers: {
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload;
    },
    clearCurrentForm: (state) => {
      state.currentForm = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Patch just the saved partition selection on the loaded form.
     *
     * Deliberately narrow. Replacing currentForm wholesale with an update
     * response drops the hydrated form_metrics that the fill page renders
     * from, which blanks the page until a refetch lands.
     */
    defaultPartitionSelectionChanged: (state, action) => {
      const { id, selection } = action.payload;
      if (state.currentForm?.id === id) {
        state.currentForm.default_partition_selection = selection;
      }
      if (state.entities?.[id]) {
        state.entities[id].default_partition_selection = selection;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Forms
      .addCase(fetchFormsThunk.pending, (state, action) => {
        state.catalogRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormsThunk.fulfilled, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        formsAdapter.setAll(state, action.payload?.data || []);
      })
      .addCase(fetchFormsThunk.rejected, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Detail
      .addCase(fetchFormDetailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormDetailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentForm = action.payload;
        formsAdapter.upsertOne(state, action.payload);
      })
      .addCase(fetchFormDetailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createFormThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFormThunk.fulfilled, (state, action) => {
        state.loading = false;
        formsAdapter.addOne(state, action.payload);
        state.currentForm = action.payload;
      })
      .addCase(createFormThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateFormThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFormThunk.fulfilled, (state, action) => {
        state.loading = false;
        formsAdapter.upsertOne(state, action.payload);
        if (state.currentForm?.id === action.payload.id) {
          state.currentForm = action.payload;
        }
      })
      .addCase(updateFormThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteFormThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFormThunk.fulfilled, (state, action) => {
        state.loading = false;
        formsAdapter.removeOne(state, action.payload);
        if (state.currentForm?.id === action.payload) {
          state.currentForm = null;
        }
      })
      .addCase(deleteFormThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Duplicate
      .addCase(duplicateFormThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateFormThunk.fulfilled, (state, action) => {
        state.loading = false;
        formsAdapter.addOne(state, action.payload);
      })
      .addCase(duplicateFormThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reorder Metrics
      .addCase(reorderFormMetricsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reorderFormMetricsThunk.fulfilled, (state, action) => {
        state.loading = false;
        formsAdapter.upsertOne(state, action.payload);
        if (state.currentForm?.id === action.payload.id) {
          state.currentForm = action.payload;
        }
      })
      .addCase(reorderFormMetricsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Available Metrics
      .addCase(fetchAvailableMetricsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableMetricsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.availableMetrics = action.payload || [];
      })
      .addCase(fetchAvailableMetricsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentForm, clearCurrentForm, clearError, defaultPartitionSelectionChanged } = formsSlice.actions;

// Entity adapter selectors
const getFormsState = (state) => state.forms || formsAdapter.getInitialState();

export const {
  selectAll: selectAllForms,
  selectById: selectFormById,
  selectIds: selectFormIds,
} = formsAdapter.getSelectors(getFormsState);

// Backward compatibility selector
export const selectForms = selectAllForms;

// Custom selectors
export const selectCurrentForm = (state) => state.forms?.currentForm || null;
export const selectFormsLoading = (state) => state.forms?.loading || false;
export const selectFormsError = (state) => state.forms?.error || null;
export const selectAvailableMetrics = (state) => state.forms?.availableMetrics || [];

/**
 * Selector to get current form with enriched form_metrics
 * Returns form with form_metrics containing full metric data
 *
 * Memoized with createSelector: without this, a new object was constructed on
 * every call (i.e. on every dispatched action, not just forms-related ones),
 * so any component depending on this selector's *identity* (e.g. via a
 * useMemo/useEffect dependency) would re-run on every store update — most
 * damagingly, on the very metric-entry fetch actions FormFillPage dispatches
 * while loading existing entries, which reset and restarted the fetch before
 * it could complete.
 */
export const selectCurrentFormWithMetrics = createSelector(
  [selectCurrentForm],
  (form) => {
    if (!form || !form._formMetrics) {
      return form;
    }

    return {
      ...form,
      form_metrics: form._formMetrics || []
    };
  }
);

/**
 * Selector to get available metrics grouped by partition signature
 * Each group contains metrics that can be combined in a form
 */
export const selectAvailableMetricsGrouped = (state) => {
  return state.forms?.availableMetrics || [];
};

/**
 * Selector to get flat list of all available metrics
 */
export const selectAvailableMetricsFlat = (state) => {
  const groups = state.forms?.availableMetrics || [];
  return groups.flatMap(group => group.metrics);
};

export default formsSlice.reducer;
